import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { formatSSNInput } from "@/lib/utils";
import { fetchFullSsns, isMaskedSsn, pickRevealedSsn } from "@/lib/ssnReveal";

/**
 * SSN edit field for a masked API value (DEV-351 follow-up). While the value
 * is masked it is read-only with an "Edit" button that fetches the full SSN
 * (audited); `onRevealed` gets the original so the save can tell "unchanged".
 * Pass `dependentId` to reveal that dependent's SSN instead of the employee's.
 */
export function SsnInput({
  id,
  employeeId,
  dependentId,
  value,
  onChange,
  onRevealed,
  className,
  disabled,
  required,
}: {
  id?: string;
  employeeId: number;
  dependentId?: number;
  value: string;
  onChange: (value: string) => void;
  onRevealed?: (ssn: string | null) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  if (isMaskedSsn(value)) {
    const reveal = async () => {
      setLoading(true);
      try {
        const ssn = pickRevealedSsn(await fetchFullSsns(employeeId, "edit"), dependentId);
        onRevealed?.(ssn);
        onChange(formatSSNInput(ssn ?? ""));
      } catch {
        toast({ title: "Could not load SSN", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    return (
      <div className={`flex items-center gap-2 ${className ?? ""}`}>
        <Input id={id} value={value} readOnly disabled={disabled} className="font-mono" />
        <Button type="button" variant="outline" size="sm" onClick={reveal} disabled={loading || disabled}>
          {loading ? "Loading…" : "Edit"}
        </Button>
      </div>
    );
  }

  return (
    <Input
      id={id}
      value={formatSSNInput(value || "")}
      onChange={(e) => onChange(formatSSNInput(e.target.value))}
      placeholder="XXX-XX-XXXX"
      inputMode="numeric"
      maxLength={11}
      className={className}
      disabled={disabled}
      required={required}
    />
  );
}
